import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object;
  robots?: string;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  jsonLd,
  robots
}: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    // Update document title
    document.title = title;

    // Remove existing meta tags
    const existingMetas = document.querySelectorAll('meta[data-seo="true"]');
    existingMetas.forEach(meta => meta.remove());

    // Remove existing JSON-LD scripts
    const existingJsonLd = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
    existingJsonLd.forEach(script => script.remove());

    // Add meta description
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = description;
    metaDescription.setAttribute('data-seo', 'true');
    document.head.appendChild(metaDescription);

    // Add keywords if provided
    if (keywords) {
      const metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = keywords;
      metaKeywords.setAttribute('data-seo', 'true');
      document.head.appendChild(metaKeywords);
    }

    // Add robots meta tag if provided
    if (robots) {
      const metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = robots;
      metaRobots.setAttribute('data-seo', 'true');
      document.head.appendChild(metaRobots);
    }

    // Add canonical URL
    const canonicalUrl = canonical || `${window.location.origin}${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      linkCanonical.setAttribute('data-seo', 'true');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:site_name', content: 'ChessHatch' },
      { property: 'og:image', content: ogImage || `${window.location.origin}/lovable-uploads/f514beef-6336-4f46-9401-6c98ac57b26e.png` }
    ];

    ogTags.forEach(({ property, content }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.content = content;
      meta.setAttribute('data-seo', 'true');
      document.head.appendChild(meta);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage || `${window.location.origin}/lovable-uploads/f514beef-6336-4f46-9401-6c98ac57b26e.png` },
      { name: 'twitter:site', content: '@ChessHatch' }
    ];

    twitterTags.forEach(({ name, content }) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      meta.setAttribute('data-seo', 'true');
      document.head.appendChild(meta);
    });

    // Add JSON-LD structured data if provided
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      script.setAttribute('data-seo', 'true');
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Don't remove metas on cleanup to prevent flickering
      // They will be replaced by the next useSEO call
    };
  }, [title, description, keywords, canonical, ogImage, ogType, jsonLd, robots, location.pathname]);
}

// Utility function to generate JSON-LD for different content types
export function generateWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ChessHatch",
    "alternateName": ["Chess Hatch", "ChessHatch.com"],
    "description": "Interactive chess learning platform for all ages. Master chess with AI opponents, tutorials, puzzles, and multiplayer games.",
    "url": window.location.origin,
    "creator": {
      "@type": "Person",
      "name": "Ved",
      "description": "Young chess enthusiast from Australia"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${window.location.origin}/analysis?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://twitter.com/ChessHatch",
      "https://github.com/ChessHatch"
    ]
  };
}

export function generateOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ChessHatch",
    "alternateName": ["Chess Hatch"],
    "description": "Interactive chess learning platform designed for young players and chess enthusiasts of all skill levels.",
    "url": window.location.origin,
    "logo": `${window.location.origin}/lovable-uploads/f514beef-6336-4f46-9401-6c98ac57b26e.png`,
    "foundingDate": "2024",
    "founder": {
      "@type": "Person",
      "name": "Ved",
      "nationality": "Australian",
      "description": "Young chess enthusiast and platform creator"
    },
    "applicationCategory": "GameApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "InStock"
    }
  };
}

export function generateBreadcrumbJsonLd(items: Array<{name: string, url: string}>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}

export function generateEducationalJsonLd(title: string, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "ChessHatch",
    "description": description,
    "url": window.location.href,
    "educationalCredentialAwarded": "Chess Skills Certification",
    "hasEducationalUse": "Chess Training",
    "learningResourceType": "Interactive Tutorial",
    "educationalLevel": "Beginner to Advanced"
  };
}

export function generateGameJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Game",
    "name": "ChessHatch Online Chess",
    "description": "Play chess online with AI opponents and real players. Suitable for all skill levels.",
    "genre": "Strategy Game",
    "gamePlatform": "Web Browser", 
    "numberOfPlayers": "1-2",
    "playMode": ["SinglePlayer", "MultiPlayer"],
    "applicationCategory": "GameApplication",
    "operatingSystem": "Any",
    "requirements": "Web Browser with JavaScript enabled",
    "featureList": [
      "AI Chess Opponents",
      "Multiplayer Chess Games",
      "Chess Analysis Engine",
      "Interactive Learning",
      "Private Game Rooms"
    ]
  };
}

export function generateSoftwareApplicationJsonLd(name: string, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "applicationCategory": "GameApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5"
    }
  };
}

export function generateFAQJsonLd(questions: Array<{question: string, answer: string}>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(qa => ({
      "@type": "Question",
      "name": qa.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": qa.answer
      }
    }))
  };
}